import { api, TPerson } from '@verifime/api-definition';
import { DOCUMENT_TYPES, DocumentType, stringUtils, TDocument } from '@verifime/utils';
import { TIdentityDocumentsUploadConfig } from '../types';

export type TDisplayMode = 'fileUploaderThenFormEditor' | 'fileUploaderAndFormEditor';

export interface TSwitchModeConfig {
  to: TDisplayMode;
  props: {
    message: string;
  };
}

export interface TDocumentTypeConfigResponse {
  type: TDocument;
  displayMode: TDisplayMode;
  uploadRequired: boolean;
  switchMode?: {
    to: TDisplayMode;
    props: {
      message: string;
    };
  };
}

interface TUseDocumentTypeConfigProps {
  documentType: DocumentType;
  customer: TPerson;
  documentTypeChoices: TIdentityDocumentsUploadConfig['documentTypeChoices'];
}

type TUploadFile = 'hasUploadFiles' | 'noUploadFiles';
type THasDetails = 'hasDetails' | 'noDetails';
type TUploadRequired = 'uploadRequired' | 'uploadNotRequired';

type TConfigMapKey = `${TUploadFile}-${THasDetails}-${TUploadRequired}`;

interface TConfigMapValue {
  displayMode: TDisplayMode;
  uploadRequired: boolean;
  switchMode?: TSwitchModeConfig;
}

const configMap: Record<TConfigMapKey, TConfigMapValue> = {
  'hasUploadFiles-hasDetails-uploadRequired': {
    displayMode: 'fileUploaderAndFormEditor',
    uploadRequired: true,
  },
  'hasUploadFiles-noDetails-uploadRequired': {
    displayMode: 'fileUploaderThenFormEditor',
    uploadRequired: true,
  },
  'noUploadFiles-hasDetails-uploadRequired': {
    displayMode: 'fileUploaderThenFormEditor',
    uploadRequired: true,
  },
  'noUploadFiles-noDetails-uploadRequired': {
    displayMode: 'fileUploaderThenFormEditor',
    uploadRequired: true,
  },
  'hasUploadFiles-hasDetails-uploadNotRequired': {
    displayMode: 'fileUploaderAndFormEditor',
    uploadRequired: false,
  },
  'hasUploadFiles-noDetails-uploadNotRequired': {
    displayMode: 'fileUploaderThenFormEditor',
    uploadRequired: false,
    switchMode: {
      to: 'fileUploaderAndFormEditor',
      props: {
        message: 'I‘m unable to upload a copy of my document',
      },
    },
  },
  'noUploadFiles-hasDetails-uploadNotRequired': {
    displayMode: 'fileUploaderAndFormEditor',
    uploadRequired: false,
  },
  'noUploadFiles-noDetails-uploadNotRequired': {
    displayMode: 'fileUploaderThenFormEditor',
    uploadRequired: false,
    switchMode: {
      to: 'fileUploaderAndFormEditor',
      props: { message: 'I‘m unable to upload a copy of my document' },
    },
  },
};

// Condition 1. If a user uploaded files (Yes/No)
const checkUploadedFiles = async (customerId: string, documentType: DocumentType) => {
  const listFiles = await api.getV1fileEntityIdDocType({
    params: { entityId: customerId, docType: documentType },
  });

  if (listFiles.itemList?.length < 1) {
    return false;
  }
  return true;
};

// Condition 2. If the customer has already document details (Yes/No)
const checkDocumentDetails = (customer: TPerson, documentType: DocumentType) => {
  const documentFieldName = stringUtils.lowerCaseFirstLetter(documentType);
  if (!(documentFieldName in customer)) {
    return false;
  }
  if (Object.keys(customer[documentFieldName] || {}).length < 1) {
    return false;
  }
  return true;
};

// Condition 3. If the configuration is uploadRequired (Yes/No)
const checkUploadRequiredConfig = (
  documentTypeChoices: TIdentityDocumentsUploadConfig['documentTypeChoices'],
  documentType: DocumentType,
) => {
  const docTypeConfig = documentTypeChoices.find((choice) => choice.type.code === documentType);
  return docTypeConfig?.rules?.uploadRequired;
};

const generateConfigKey = async ({
  documentType,
  customer,
  documentTypeChoices,
}: TUseDocumentTypeConfigProps): Promise<TConfigMapKey> => {
  const hasUploadedFiles = await checkUploadedFiles(customer.id, documentType);
  const hasDocumentDetails = checkDocumentDetails(customer, documentType);
  const isUploadRequired = checkUploadRequiredConfig(documentTypeChoices, documentType);

  const uploadFileStatus: TUploadFile = hasUploadedFiles ? 'hasUploadFiles' : 'noUploadFiles';
  const detailsStatus: THasDetails = hasDocumentDetails ? 'hasDetails' : 'noDetails';
  const uploadRequiredStatus: TUploadRequired = isUploadRequired
    ? 'uploadRequired'
    : 'uploadNotRequired';

  return `${uploadFileStatus}-${detailsStatus}-${uploadRequiredStatus}`;
};

export const determineMode = async ({
  documentType,
  customer,
  documentTypeChoices,
}: TUseDocumentTypeConfigProps): Promise<TDocumentTypeConfigResponse> => {
  const config: TDocumentTypeConfigResponse = {
    type: DOCUMENT_TYPES[documentType],
    displayMode: 'fileUploaderThenFormEditor',
    uploadRequired: true,
    switchMode: undefined,
  };

  if (!documentType || !customer || !documentTypeChoices) {
    return config;
  }

  const configKey = await generateConfigKey({
    documentType,
    customer,
    documentTypeChoices,
  });
  const configForScenario = configMap[configKey];
  config.displayMode = configForScenario.displayMode;
  config.uploadRequired = configForScenario.uploadRequired;
  if (configForScenario.switchMode) {
    config.switchMode = configForScenario.switchMode;
  }
  return config;
};
